import React from 'react';
import { Sparkles } from 'lucide-react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../../types/chat';
import { ChatMessage } from '../ChatMessage';
import { ChatLoadingIndicator } from '../ChatLoadingIndicator';
import { Spinner } from '../../ui/Spinner';
import { CHAT_TEXT } from '../../../constants/chat';

interface ChatMessagesAreaProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  onExecuteAction: (action: ChatAction) => void;
  messagesEndRef?: React.RefObject<HTMLDivElement>;
  // Optional slots for flow components and progress
  progressIndicator?: React.ReactNode;
  editableCriteriaSection?: React.ReactNode;
  flowComponent?: React.ReactNode;
  // Styling
  className?: string;
  variant?: 'embedded' | 'panel';
}

/**
 * Messages display area with loading states and optional slots
 * Used in both embedded and sliding panel modes
 */
export const ChatMessagesArea: React.FC<ChatMessagesAreaProps> = ({
  messages,
  isLoading,
  isLoadingHistory,
  onExecuteAction,
  messagesEndRef,
  progressIndicator,
  editableCriteriaSection,
  flowComponent,
  className = '',
  variant = 'embedded',
}) => {
  // If a flow component is provided, render it instead of messages
  if (flowComponent) {
    return (
      <div className={`flex-1 overflow-hidden ${variant === 'panel' ? 'p-4' : ''} ${className}`}>
        {flowComponent}
      </div>
    );
  }

  // Loading history state
  if (isLoadingHistory) {
    return (
      <div className={`flex-1 flex items-center justify-center ${className}`}>
        <Spinner size="md" text="Loading chat..." center />
      </div>
    );
  }

  // Empty state
  if (messages.length === 0 && !progressIndicator && !editableCriteriaSection) {
    return (
      <div className={`flex-1 flex flex-col items-center justify-center text-center px-4 ${className}`}>
        <div className={`${variant === 'panel' ? 'w-12 h-12 mb-4' : 'w-10 h-10 mb-3'} bg-gray-100 rounded-full flex items-center justify-center`}>
          <Sparkles className={`${variant === 'panel' ? 'w-5 h-5' : 'w-4 h-4'} text-gray-400`} />
        </div>
        <h4 className="text-[14px] font-medium text-gray-700 mb-1">{CHAT_TEXT.EMPTY_STATE_TITLE}</h4>
        <p className="text-[13px] text-gray-500 leading-relaxed">
          {CHAT_TEXT.EMPTY_STATE_DESCRIPTION}
        </p>
      </div>
    );
  }

  // Messages display
  return (
    <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${className}`}>
      {messages.map((message) => (
        <ChatMessage
          key={message.id}
          message={message}
          onExecuteAction={onExecuteAction}
        />
      ))}

      {/* Progress indicator slot */}
      {progressIndicator}

      {/* Editable criteria slot */}
      {editableCriteriaSection}

      {/* Loading indicator */}
      {isLoading && <ChatLoadingIndicator />}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
};
